import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { MainLayout } from "./components/layout/MainLayout";
import { CurrencyProvider } from "./context/CurrencyContext";
import Home from "./pages/Home";
import About from "./pages/About";
import InstrumentsLevels from "./pages/InstrumentsLevels";
import ExaminationSystem from "./pages/ExaminationSystem";
import TeachersEducators from "./pages/TeachersEducators";
import AuthorizedTeacher from "./pages/AuthorizedTeacher";
import TeacherResources from "./pages/TeacherResources";
import StudentAccount from "./pages/StudentAccount";
import AssessmentCriteria from "./pages/AssessmentCriteria";
import CertificationRecognition from "./pages/CertificationRecognition";
import ExaminerPanel from "./pages/ExaminerPanel";
import FeesSchedule from "./pages/FeesSchedule";
import Faqs from "./pages/Faqs";
import ContactSupport from "./pages/ContactSupport";
import SyllabusStructure from "./pages/SyllabusStructure";
import GuitarSyllabusOverview from "./pages/GuitarSyllabusOverview";
import GuitarLevelDetail from "./pages/GuitarLevelDetail";
import PianoSyllabusOverview from "./pages/PianoSyllabusOverview";
import PianoLevelDetail from "./pages/PianoLevelDetail";
import DrumsSyllabusOverview from "./pages/DrumsSyllabusOverview";
import DrumsLevelDetail from "./pages/DrumsLevelDetail";
import ViolinSyllabusOverview from "./pages/ViolinSyllabusOverview";
import ViolinLevelDetail from "./pages/ViolinLevelDetail";
import AltoSaxophoneSyllabusOverview from "./pages/AltoSaxophoneSyllabusOverview";
import AltoSaxophoneLevelDetail from "./pages/AltoSaxophoneLevelDetail";
import PianoDetail from "./pages/instruments/PianoDetail";
import GuitarDetail from "./pages/instruments/GuitarDetail";
import ViolinDetail from "./pages/instruments/ViolinDetail";
import DrumsDetail from "./pages/instruments/DrumsDetail";
import VocalsDetail from "./pages/instruments/VocalsDetail";
import VocalsSyllabusOverview from "./pages/VocalsSyllabusOverview";
import VocalsLevelDetail from "./pages/VocalsLevelDetail";
import AIVoiceSyllabus from "./pages/AIVoiceSyllabus";
import MusicTherapySyllabus from "./pages/MusicTherapySyllabus";
import MusicScholarship from "./pages/MusicScholarship";
import StudentScholarshipForm from "./pages/StudentScholarshipForm";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";

// Router wrapper to handle auth callback
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Auth routes */}
      <Route path="/login" element={<Login />} />
      
      {/* Dashboard with MainLayout */}
      <Route path="/dashboard" element={<MainLayout />}>
        <Route index element={<Dashboard />} />
      </Route>
      
      {/* Main routes with MainLayout */}
      <Route path="/" element={<MainLayout />}>
        <Route index element={<Home />} />
        <Route path="about" element={<About />} />
        <Route path="music-scholarship" element={<MusicScholarship />} />
        <Route path="student-scholarship" element={<StudentScholarshipForm />} />
        <Route path="instruments-levels" element={<InstrumentsLevels />} />
        <Route path="examination-system" element={<ExaminationSystem />} />
        <Route path="syllabus-structure" element={<SyllabusStructure />} />
        <Route path="teachers-educators" element={<TeachersEducators />} />
        <Route path="authorized-teacher" element={<AuthorizedTeacher />} />
        <Route path="teacher-resources" element={<TeacherResources />} />
        <Route path="student-account" element={<StudentAccount />} />
        <Route path="assessment-criteria" element={<AssessmentCriteria />} />
        <Route path="certification-recognition" element={<CertificationRecognition />} />
        <Route path="examiner-panel" element={<ExaminerPanel />} />
        <Route path="fees-schedule" element={<FeesSchedule />} />
        <Route path="faqs" element={<Faqs />} />
        <Route path="contact-support" element={<ContactSupport />} />

        {/* Guitar syllabus pages */}
        <Route path="guitar-syllabus" element={<GuitarSyllabusOverview />} />
        <Route path="guitar-syllabus/level/:levelNumber" element={<GuitarLevelDetail />} />

        {/* Piano syllabus pages */}
        <Route path="piano-syllabus" element={<PianoSyllabusOverview />} />
        <Route path="piano-syllabus/level/:levelNumber" element={<PianoLevelDetail />} />

        {/* Drum kit syllabus pages */}
        <Route path="drums-syllabus" element={<DrumsSyllabusOverview />} />
        <Route path="drums-syllabus/level/:levelNumber" element={<DrumsLevelDetail />} />

        {/* Violin syllabus pages */}
        <Route path="violin-syllabus" element={<ViolinSyllabusOverview />} />
        <Route path="violin-syllabus/level/:levelNumber" element={<ViolinLevelDetail />} />

        {/* Vocals syllabus pages (with specialisations) */}
        <Route path="vocals-syllabus" element={<VocalsSyllabusOverview />} />
        <Route path="vocals-syllabus/:categoryId/level/:levelNumber" element={<VocalsLevelDetail />} />

        {/* Alto Saxophone syllabus pages */}
        <Route path="alto-saxophone-syllabus" element={<AltoSaxophoneSyllabusOverview />} />
        <Route path="alto-saxophone-syllabus/level/:levelNumber" element={<AltoSaxophoneLevelDetail />} />

        {/* AI Voice Model Trainer syllabus page */}
        <Route path="ai-voice-syllabus" element={<AIVoiceSyllabus />} />

        {/* Music Therapy syllabus page */}
        <Route path="music-therapy-syllabus" element={<MusicTherapySyllabus />} />

        {/* Instrument detail pages (frontend-only, using shared level descriptors) */}
        <Route path="instruments/piano" element={<PianoDetail />} />
        <Route path="instruments/guitar" element={<GuitarDetail />} />
        <Route path="instruments/violin" element={<ViolinDetail />} />
        <Route path="instruments/drums" element={<DrumsDetail />} />
        <Route path="instruments/vocals" element={<VocalsDetail />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <CurrencyProvider>
      <div className="min-h-screen bg-slate-50 text-slate-900">
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster position="top-center" richColors closeButton />
      </div>
    </CurrencyProvider>
  );
}

export default App;
